import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, signal } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { ButtonModule } from "primeng/button";
import { InputErrorComponent } from "../../../../../../../shared/components/input-error/input-error.component";
import { isValidEmail } from "../../../../../../../shared/utils/email-helper";
import { UserVendorContact } from "../../../../../../../core/database/db";

export type EditVendorContactMode = "add" | "edit";
export type EditVendorContactAction = "save" | "saveAndAddAnother" | "delete";

export interface EditVendorContactResult {
  email: string;
  mode: EditVendorContactMode;
}

@Component({
  selector: "app-edit-vendor-contact",
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonModule, InputErrorComponent],
  templateUrl: "./edit-vendor-contact.component.html",
  styleUrls: ["./edit-vendor-contact.component.scss"]
})
export class EditVendorContactComponent implements OnChanges {
  @Input() mode: EditVendorContactMode = "add";
  @Input() contact: UserVendorContact | null = null;
  @Input() vendorName: string = "";
  @Input() isSaving: boolean = false;
  @Input() loadingAction: EditVendorContactAction | null = null;
  @Input() resetVersion: number = 0;

  @Output() save = new EventEmitter<EditVendorContactResult>();
  @Output() saveAndAddAnother = new EventEmitter<EditVendorContactResult>();
  @Output() delete = new EventEmitter<UserVendorContact>();
  @Output() cancel = new EventEmitter<void>();

  emailValue = signal<string>("");
  submitted = signal<boolean>(false);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["contact"] || changes["mode"] || changes["resetVersion"]) {
      this.emailValue.set(this.mode === "edit" && this.contact ? this.contact.email : "");
      this.submitted.set(false);
    }
  }

  get emailError(): string | null {
    if (!this.submitted()) {
      return null;
    }
    const email = this.emailValue().trim();
    if (!email) {
      return "Email address is required.";
    }
    if (!isValidEmail(email)) {
      return "Please enter a valid email address.";
    }
    return null;
  }

  onSave(): void {
    if (this.isSaving) {
      return;
    }
    this.submitted.set(true);
    if (!isValidEmail(this.emailValue().trim())) {
      return;
    }

    this.save.emit({
      email: this.emailValue().trim(),
      mode: this.mode
    });
  }

  onSaveAndAddAnother(): void {
    if (this.isSaving) {
      return;
    }
    this.submitted.set(true);
    if (!isValidEmail(this.emailValue().trim())) {
      return;
    }

    this.saveAndAddAnother.emit({
      email: this.emailValue().trim(),
      mode: this.mode
    });
  }

  onDelete(): void {
    if (this.isSaving) {
      return;
    }
    if (this.contact) {
      this.delete.emit(this.contact);
    }
  }

  onCancel(): void {
    if (this.isSaving) {
      return;
    }
    this.reset();
    this.cancel.emit();
  }

  private reset(): void {
    this.emailValue.set("");
    this.submitted.set(false);
  }
}
